import { stripDisabledNameFields } from '@welbe/design-system';
import { GENERIC_CURP } from 'constants/genericNationalIdentity';
import { mapBiologicalSexToCurpSex } from 'helpers/curp';
import { handleToast } from 'helpers/handleToast';
import { useBranchOffices } from 'hooks/useBranchOffices';
import { useSessionStorage } from 'hooks/useSessionStorage';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import {
	getCountriesCatalog,
	getFederalEntitiesCatalog,
	getLocationsCatalog,
	getMunicipalitiesCatalog,
	getNationalitiesCatalog,
	postValidateCurp,
} from 'services/services';
import { sanitize } from 'utils/sanitize';
import { formatErrorResponse, validate } from 'utils/zodValidation';
import {
	DEFAULT_FORM_VALUES,
	IGNORED_ENTITY_NAME,
	MEXICO_COUNTRY_CODE,
	SPECIAL_ENTITY_MAP,
	UNSPECIFIED_ENTITY_NAME,
} from './DoctorDrawer.constants';
import { mapDoctorToFormValues, toRequestCode } from './DoctorDrawer.mappers';
import { doctorDrawerSchema } from './DoctorDrawer.schema';
import {
	getDoctorBranchOffices,
	getPersonalTypes,
	getSpecialties,
	postDoctor,
	putDoctor,
} from './DoctorDrawer.services';
import { DoctorDrawerProps } from './DoctorDrawer.types';

type CatalogOption = { id: string; name: string };

export const useDoctorDrawer = ({ open, user, setOpen }: DoctorDrawerProps) => {
	const { getSessionStorage } = useSessionStorage();
	const { branchOfficeOptions, isLoading } = useBranchOffices();
	const [fieldsWithErrors, setFieldsWithErrors] = useState<
		Record<string, string>
	>({});
	const [isRequestLoading, setRequestLoading] = useState<boolean>(false);
	const [isPersonalTypesLoading, setPersonalTypesLoading] =
		useState<boolean>(false);
	const [personalTypes, setPersonalTypes] = useState<any[]>([]);
	const hasSires = useMemo(
		() =>
			Boolean(
				JSON.parse(getSessionStorage('me') || '{}')?.company
					?.companyPortalSettings?.hasSires,
			),
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[],
	);
	const [isSpecialtiesLoading, setSpecialtiesLoading] =
		useState<boolean>(false);
	const [specialties, setSpecialties] = useState<any[]>([]);
	const [countries, setCountries] = useState<CatalogOption[]>([]);
	const [isCountriesLoading, setCountriesLoading] = useState<boolean>(false);
	const [federalEntities, setFederalEntities] = useState<CatalogOption[]>([]);
	const [isFederalEntitiesLoading, setFederalEntitiesLoading] =
		useState<boolean>(false);
	const [nationalities, setNationalities] = useState<CatalogOption[]>([]);
	const [isNationalitiesLoading, setNationalitiesLoading] =
		useState<boolean>(false);
	const [residenceCountries, setResidenceCountries] = useState<CatalogOption[]>(
		[],
	);
	const [isResidenceCountriesLoading, setResidenceCountriesLoading] =
		useState<boolean>(false);
	const [residenceFederalEntities, setResidenceFederalEntities] = useState<
		CatalogOption[]
	>([]);
	const [
		isResidenceFederalEntitiesLoading,
		setResidenceFederalEntitiesLoading,
	] = useState<boolean>(false);
	const [municipalities, setMunicipalities] = useState<CatalogOption[]>([]);
	const [isMunicipalitiesLoading, setMunicipalitiesLoading] =
		useState<boolean>(false);
	const [localities, setLocalities] = useState<CatalogOption[]>([]);
	const [isLocalitiesLoading, setLocalitiesLoading] = useState<boolean>(false);
	const [isBranchOfficesLoading, setBranchOfficesLoading] =
		useState<boolean>(false);

	const form = useForm({
		defaultValues: DEFAULT_FORM_VALUES,
	});

	const { register, control, setValue, getValues, watch, reset } = form;

	const countryValue = watch('countryResidenceCode');
	const federalEntityValue = watch('federalEntityResidenceCode');
	const birthCountryCode = watch('countryCode');
	const birthFederalEntityCode = watch('federalEntityCode');

	const isMexico = useMemo(
		() => Number(countryValue) === MEXICO_COUNTRY_CODE,
		[countryValue],
	);

	const isBirthCountryMexico = useMemo(
		() => Number(birthCountryCode) === MEXICO_COUNTRY_CODE,
		[birthCountryCode],
	);

	const unspecifiedEntityId = useMemo(
		() => federalEntities.find((e) => e.name === UNSPECIFIED_ENTITY_NAME)?.id,
		[federalEntities],
	);

	const selectedBirthFederalEntity = useMemo(() => {
		if (!birthFederalEntityCode) return undefined;
		return federalEntities.find((e) => e.id === birthFederalEntityCode);
	}, [federalEntities, birthFederalEntityCode]);

	const isCurpIgnored = useMemo(
		() =>
			isBirthCountryMexico &&
			selectedBirthFederalEntity?.name === IGNORED_ENTITY_NAME,
		[isBirthCountryMexico, selectedBirthFederalEntity],
	);

	const isBirthEntityUnspecified = useMemo(
		() => selectedBirthFederalEntity?.name === UNSPECIFIED_ENTITY_NAME,
		[selectedBirthFederalEntity],
	);

	const isBirthEntityIgnored = useMemo(
		() => selectedBirthFederalEntity?.name === IGNORED_ENTITY_NAME,
		[selectedBirthFederalEntity],
	);

	const canCheckNoCurp = isBirthEntityUnspecified || isBirthEntityIgnored;

	const isSpecialEntity = useMemo(
		() =>
			Boolean(federalEntityValue) &&
			Number(federalEntityValue) in SPECIAL_ENTITY_MAP,
		[federalEntityValue],
	);

	const mappedBranchOfficeOptions = useMemo(() => {
		return branchOfficeOptions.map((branchOffice) => ({
			id: branchOffice.id,
			name: branchOffice.value,
		}));
	}, [branchOfficeOptions]);

	const editingDoctorId = user?.id ?? null;

	const isEditingDoctor = Boolean(editingDoctorId);

	const handleCountryCodeChange = useCallback(
		(value: string) => {
			if (getValues('countryCode') === value) return;

			const isMexicoSelected = Number(value) === MEXICO_COUNTRY_CODE;

			setValue('countryCode', value, { shouldDirty: true });

			setValue(
				'federalEntityCode',
				isMexicoSelected ? '' : unspecifiedEntityId ?? '',
				{ shouldDirty: true, shouldValidate: true },
			);

			if (isMexicoSelected && getValues('nationalIdentity') === GENERIC_CURP) {
				setValue('nationalIdentity', '');
			}
		},
		[setValue, getValues, unspecifiedEntityId],
	);

	const fetchMunicipalities = async (federalEntityCode: number) => {
		setMunicipalitiesLoading(true);
		try {
			const { data } = await getMunicipalitiesCatalog(federalEntityCode);
			setMunicipalities(
				data.content.municipalities.map((item: any) => ({
					id: String(item.code),
					name: item.name,
				})),
			);
		} catch (error) {
			handleToast('error', 'Hubo un error al cargar los municipios');
		} finally {
			setMunicipalitiesLoading(false);
		}
	};

	const fetchLocalities = async (
		municipalityCode: number,
		federalEntityCode: number,
	) => {
		setLocalitiesLoading(true);
		try {
			const { data } = await getLocationsCatalog(
				municipalityCode,
				federalEntityCode,
			);
			setLocalities(
				data.content.locations.map((item: any) => ({
					id: String(item.code),
					name: item.name,
				})),
			);
		} catch (error) {
			handleToast('error', 'Hubo un error al cargar las localidades');
		} finally {
			setLocalitiesLoading(false);
		}
	};

	const handleFederalEntityResidenceChange = (entityCode: number) => {
		setValue('federalEntityResidenceCode', String(entityCode));

		const special = SPECIAL_ENTITY_MAP[entityCode];
		if (special) {
			setValue('municipalityResidenceCode', String(special.municipality));
			setValue('localityResidenceCode', String(special.locality));
			setMunicipalities([]);
			setLocalities([]);
		} else {
			setValue('municipalityResidenceCode', '');
			setValue('localityResidenceCode', '');
			setLocalities([]);
			fetchMunicipalities(entityCode);
		}
	};

	const handleMunicipalityResidenceChange = (municipalityCode: number) => {
		setValue('municipalityResidenceCode', String(municipalityCode));
		setValue('localityResidenceCode', '');
		setLocalities([]);
		const entityCode = Number(getValues('federalEntityResidenceCode'));
		fetchLocalities(municipalityCode, entityCode);
	};

	const formatPayload = () => {
		const values = getValues();

		return {
			id: editingDoctorId ?? undefined,
			active: user?.active ?? false,
			professionalRole: 'Ocupacional',
			title: values.title,
			firstName: values.firstName,
			middleName: values.middleName,
			lastName: values.lastName,
			hasNoMiddleName: values.hasNoMiddleName,
			hasNoLastName: values.hasNoLastName,
			birthDate: values.birthDate,
			sex: values.sex,
			contactNumber: values.contactNumber,
			nationalIdentity: values.nationalIdentity,
			email: values.email,
			doctorPersonalTypeId: values.doctorPersonalTypeId,
			professionalLicenseId: values.professionalLicenseId,
			university: values.university,
			branchOfficeIds: values.branchOffices.map(({ id }) => id),
			specialtyIds: values.specialties.map(({ id }) => id),
			countryCode: toRequestCode(values.countryCode),
			federalEntityCode: toRequestCode(values.federalEntityCode),
			nationalityCode: toRequestCode(values.nationalityCode),
			countryResidenceCode: toRequestCode(values.countryResidenceCode),
			federalEntityResidenceCode: toRequestCode(
				values.federalEntityResidenceCode,
			),
			municipalityResidenceCode: toRequestCode(
				values.municipalityResidenceCode,
			),
			localityResidenceCode: toRequestCode(values.localityResidenceCode),
		};
	};

	const handleSaveDoctor = async () => {
		setFieldsWithErrors({});
		setRequestLoading(true);

		try {
			const formatted = formatPayload();
			const sanitized = sanitize(formatted);
			const validated = await validate(
				doctorDrawerSchema(hasSires),
				stripDisabledNameFields({
					...sanitized,
					firstName: formatted.firstName ?? '',
					middleName: formatted.middleName ?? '',
					lastName: formatted.lastName ?? '',
				}),
			);

			const payload = {
				...sanitized,
				firstName: validated.firstName,
				middleName: formatted.hasNoMiddleName
					? undefined
					: validated.middleName,
				lastName: formatted.hasNoLastName ? undefined : validated.lastName,
			};

			await postValidateCurp({
				curp: payload.nationalIdentity,
				context: {
					firstName: payload.firstName,
					middleName: payload.hasNoMiddleName ? 'XX' : payload.middleName,
					lastName: payload.hasNoLastName ? 'XX' : payload.lastName,
					birthDate: payload.birthDate,
					sex: mapBiologicalSexToCurpSex(payload.sex),
					birthStateCode: payload.federalEntityCode,
				},
			});

			if (editingDoctorId) {
				await putDoctor(editingDoctorId, payload);
			} else {
				await postDoctor(payload);
			}

			handleToast(
				'success',
				`Usuario ${isEditingDoctor ? 'actualizado' : 'creado'} exitosamente`,
				`${
					isEditingDoctor
						? 'Se ha enviado un correo electrónico con los pasos a seguir'
						: 'Se ha creado el usuário con éxito'
				} `,
			);
			setOpen(false);
		} catch (error: any) {
			console.error(error);
			const validationFieldMap: Record<string, string> = {
				'CURP validation failed': 'nationalIdentity',
				'Email validation failed': 'email',
			};

			if (error.zodError) {
				const errors = formatErrorResponse(error.zodError);
				setFieldsWithErrors(errors);
				handleToast('error', 'Rellena los campos marcados');
			} else {
				const serverMessage = error?.response?.data?.message;
				const field = validationFieldMap[serverMessage];

				if (field) {
					setFieldsWithErrors({
						[field]: error.response.data.errors[0].message,
					});
					handleToast('error', 'Rellena los campos marcados');
				} else {
					handleToast(
						'error',
						'Hubo un error al intentar realizar esta acción',
					);
				}
			}
		} finally {
			setRequestLoading(false);
		}
	};

	const fetchNationalities = async () => {
		setNationalitiesLoading(true);
		try {
			const { data } = await getNationalitiesCatalog();
			setNationalities(
				data.content.nationalities.map((item: any) => ({
					id: String(item.code),
					name: item.nationality,
				})),
			);
		} catch (error) {
			handleToast('error', 'Hubo un error al cargar las nacionalidades');
		} finally {
			setNationalitiesLoading(false);
		}
	};

	const fetchDoctorBranchOffices = async (doctorId: string) => {
		setBranchOfficesLoading(true);
		try {
			const { data } = await getDoctorBranchOffices(doctorId);
			setValue('branchOffices', data.content.branchOffices);
		} catch {
			handleToast('error', 'Hubo un error al cargar las sucursales del médico');
		} finally {
			setBranchOfficesLoading(false);
		}
	};

	const fetchSpecialties = async () => {
		setSpecialtiesLoading(true);
		try {
			const { data } = await getSpecialties();
			setSpecialties(data.content);
		} catch (error) {
			handleToast('error', 'Hubo un error al cargar las especialidades');
		} finally {
			setSpecialtiesLoading(false);
		}
	};

	const fetchCountries = async () => {
		setCountriesLoading(true);
		setResidenceCountriesLoading(true);
		try {
			const { data } = await getCountriesCatalog();
			const items = data.content.countries.map((c: any) => ({
				id: String(c.code),
				name: c.country,
			}));
			setCountries(items);
			setResidenceCountries(items);
		} catch (error) {
			handleToast('error', 'Hubo un error al cargar los países');
		} finally {
			setCountriesLoading(false);
			setResidenceCountriesLoading(false);
		}
	};

	const fetchFederalEntities = async () => {
		setFederalEntitiesLoading(true);
		setResidenceFederalEntitiesLoading(true);
		try {
			const { data } = await getFederalEntitiesCatalog();
			const items = data.content.federalEntities.map((e: any) => ({
				id: String(e.code),
				name: e.name,
			}));
			setFederalEntities(items);
			setResidenceFederalEntities(items);
		} catch (error) {
			handleToast('error', 'Hubo un error al cargar las entidades federativas');
		} finally {
			setFederalEntitiesLoading(false);
			setResidenceFederalEntitiesLoading(false);
		}
	};

	const fetchPersonalTypes = async () => {
		setPersonalTypesLoading(true);
		try {
			const { data } = await getPersonalTypes({ hasSires });
			setPersonalTypes(data.content);
		} catch (error) {
			handleToast(
				'error',
				'Hubo un error al cargar los tipos de profesional médico',
			);
		} finally {
			setPersonalTypesLoading(false);
		}
	};

	useEffect(() => {
		fetchCountries();
		fetchFederalEntities();
		fetchNationalities();
		fetchPersonalTypes();
		fetchSpecialties();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useEffect(() => {
		if (!open) return;

		setFieldsWithErrors({});

		if (!user) {
			reset(DEFAULT_FORM_VALUES);
			return;
		}

		const populateFromUser = async () => {
			reset(mapDoctorToFormValues(user));

			if (user.id) {
				fetchDoctorBranchOffices(user.id);
			}

			const isResidenceMexico =
				Number(user.countryResidenceCode) === MEXICO_COUNTRY_CODE;
			const fed = user.federalEntityResidenceCode;
			const mun = user.municipalityResidenceCode;
			if (isResidenceMexico && fed && !(Number(fed) in SPECIAL_ENTITY_MAP)) {
				await fetchMunicipalities(Number(fed));
				if (mun) {
					await fetchLocalities(Number(mun), Number(fed));
				}
			}
		};

		populateFromUser();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [open, user]);

	/**
	 * Fora do México só existe "NO ESPECIFICADO" como entidad de nacimiento.
	 * O efeito cobre os casos que o handler não alcança: catálogo que carrega
	 * depois da hidratação e médico estrangeiro salvo com outra entidade.
	 */
	useEffect(() => {
		if (!open || !birthCountryCode || isBirthCountryMexico) return;
		if (!unspecifiedEntityId) return;

		if (getValues('federalEntityCode') !== unspecifiedEntityId) {
			setValue('federalEntityCode', unspecifiedEntityId, {
				shouldValidate: true,
			});
		}
	}, [
		open,
		birthCountryCode,
		isBirthCountryMexico,
		unspecifiedEntityId,
		getValues,
		setValue,
	]);

	useEffect(() => {
		const current = getValues('nationalIdentity');
		if (isCurpIgnored) {
			if (current !== GENERIC_CURP) {
				setValue('nationalIdentity', GENERIC_CURP);
			}
		} else if (current === GENERIC_CURP) {
			setValue('nationalIdentity', '');
		}
	}, [isCurpIgnored, setValue, getValues]);

	return {
		isEditingDoctor,
		fieldsWithErrors,
		isRequestLoading,
		register,
		control,
		setValue,
		watch,
		handleSaveDoctor,
		personalTypes,
		isPersonalTypesLoading,
		specialties,
		isSpecialtiesLoading,
		mappedBranchOfficeOptions,
		isLoading,
		isBranchOfficesLoading,
		countries,
		isCountriesLoading,
		federalEntities,
		isFederalEntitiesLoading,
		hasSires,
		nationalities,
		isNationalitiesLoading,
		residenceCountries,
		isResidenceCountriesLoading,
		residenceFederalEntities,
		isResidenceFederalEntitiesLoading,
		municipalities,
		isMunicipalitiesLoading,
		localities,
		isLocalitiesLoading,
		isMexico,
		isSpecialEntity,
		isBirthCountryMexico,
		isCurpIgnored,
		isBirthEntityUnspecified,
		canCheckNoCurp,
		handleCountryCodeChange,
		handleFederalEntityResidenceChange,
		handleMunicipalityResidenceChange,
		fetchMunicipalities,
		fetchLocalities,
	};
};
